import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { MapPin, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/common/components/ui/button';
import {
  billingAddressQueries,
  billingAddressQueryKeys,
} from '@/features/billing-addresses/queries';
import { BillingAddressService } from '@/features/billing-addresses/services/BillingAddressService';
import { useBillingAddressDialog } from '@/features/billing-addresses/components/billing-address-dialog/useBillingAddressDialog';
import { BillingAddressCard } from '@/features/billing-addresses/components/billing-address-card/BillingAddressCard';
import type { BillingAddress } from '@/features/billing-addresses/services/schemas';
import type { Organization } from '@/features/organizations/services/schemas';
import { useConfirmDialog } from '@/common/components/confirm-dialog/useConfirmDialog.tsx';

export function OrgBillingTab({ org, orgId }: { org: Organization; orgId: number }) {
  const queryClient = useQueryClient();
  const confirm = useConfirmDialog();
  const openBillingAddressDialog = useBillingAddressDialog([org]);

  const { data: allAddresses } = useSuspenseQuery(billingAddressQueries.list());
  const addresses = allAddresses.filter((a) => a.partner === orgId);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => BillingAddressService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingAddressQueryKeys.all });
    },
    onError: () => toast.error(t`Failed to delete address`),
  });

  const handleAdd = async () => {
    const saved = await openBillingAddressDialog({ owner: { orgId } });
    if (saved) queryClient.invalidateQueries({ queryKey: billingAddressQueryKeys.all });
  };

  const handleEdit = async (address: BillingAddress) => {
    const saved = await openBillingAddressDialog({ address });
    if (saved) queryClient.invalidateQueries({ queryKey: billingAddressQueryKeys.all });
  };

  const canEditAddress = (address: BillingAddress) => address.authorizedRole === 'full';

  const handleDelete = async (address: BillingAddress) => {
    const name = address.name ?? '';
    const confirmed = await confirm({
      title: t`Delete address?`,
      description: t`"${name}" will be permanently removed.`,
      confirmLabel: t`Delete`,
    });
    if (confirmed) deleteMutation.mutate(address.id);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">
            <Trans>Billing addresses</Trans>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            <Trans>Shared with all organization members when activating products.</Trans>
          </p>
        </div>
        <Button onClick={handleAdd} className="gap-1.5">
          <Plus size={14} />
          <Trans>Add address</Trans>
        </Button>
      </div>

      {addresses.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-16 text-center">
          <MapPin size={32} className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground text-sm">
            <Trans>No billing addresses.</Trans>
          </p>
          <Button onClick={handleAdd} className="mt-4 gap-1.5">
            <Plus size={14} />
            <Trans>Add address</Trans>
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses.map((a) => (
            <BillingAddressCard
              key={a.id}
              address={a}
              onEdit={canEditAddress(a) ? handleEdit : undefined}
              onDelete={canEditAddress(a) ? handleDelete : undefined}
              readonlyMessage={
                !canEditAddress(a) ? <Trans>Only organization admin can edit</Trans> : undefined
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
